import { Component, OnInit } from '@angular/core';
import { EmailSettingsService } from './email-settings.service';
import { PlaceholderTarget } from '../placeholder-insert.component';
import { EMAIL_PLACEHOLDERS } from '../email-placeholders';

interface ConfirmationEmail {
    field: string;
    name: string;
}

/**
 * Settings > Communications > Appointment Emails & Canned Replies (mockup settings
 * #emailsCannedRepliesPanel; twin-ci settings_email + canned replies):
 *   - Default Email Container: the header and footer wrapped around every email
 *     (setting_email_template_top / _bottom);
 *   - Appointment Confirmation: the Inspection Scheduled email, one per job payment type as
 *     before (the mockup shows one template; the site sends the one for the job's payment type);
 *   - Canned Replies (app-add-canned-reply, embedded).
 * Each dialog saves only its own fields (api/auto-emails). SMTP is on SMTP Settings.
 */
@Component({
    selector: 'app-auto-emails',
    templateUrl: './auto-emails.component.html',
    styleUrls: ['./auto-emails.component.scss']
})
export class AutoEmailsComponent implements OnInit {
    readonly confirmations: ConfirmationEmail[] = [
        { field: 'setting_email_inspection_scheduled_check_html', name: 'Appointment Confirmation – Cheque' },
        { field: 'setting_email_inspection_scheduled_cc_html', name: 'Appointment Confirmation – Credit Card' },
        { field: 'setting_email_inspection_scheduled_invoice_html', name: 'Appointment Confirmation – Invoice' },
        { field: 'setting_email_inspection_scheduled_escrow_html', name: 'Appointment Confirmation – Escrow' },
    ];
    readonly placeholders = EMAIL_PLACEHOLDERS;

    values: any = null;
    loadError = '';
    message = '';

    // Default Email Container dialog
    container: { top: string, bottom: string } = null;
    containerTargets: PlaceholderTarget[] = [];
    // Appointment Confirmation dialog
    confirmation: ConfirmationEmail = null;
    confirmationBody = '';
    readonly confirmationTargets: PlaceholderTarget[] = [{ label: 'Content', editor: true }];
    showPlaceholders = false;

    errors: { [key: string]: string } = {};
    formError = '';
    saving = false;

    constructor(private emails: EmailSettingsService) { }

    ngOnInit() {
        this.emails.load().subscribe(
            res => this.values = res.values,
            err => this.loadError = typeof err === 'string' ? err : 'The email settings could not be loaded.'
        );
    }

    /** First words of an email body, as plain text, for the table. */
    preview(html: string): string {
        const text = String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        return text.length > 90 ? text.slice(0, 90) + '…' : (text || 'Empty');
    }

    // ---- Default Email Container ----

    editContainer() {
        this.open();
        this.container = { top: this.values.setting_email_template_top, bottom: this.values.setting_email_template_bottom };
    }

    /** The textareas exist once the dialog is shown; the insert tool needs them. */
    containerFields(header: HTMLTextAreaElement, footer: HTMLTextAreaElement): PlaceholderTarget[] {
        if (!this.containerTargets.length || this.containerTargets[0].input !== header) {
            this.containerTargets = [
                { label: 'Company Header', input: header, set: v => this.container.top = v },
                { label: 'Company Footer', input: footer, set: v => this.container.bottom = v },
            ];
        }
        return this.containerTargets;
    }

    saveContainer() {
        const fields = { setting_email_template_top: this.container.top, setting_email_template_bottom: this.container.bottom };
        this.errors = this.emails.blanks(fields, { setting_email_template_top: 'Company Header', setting_email_template_bottom: 'Company Footer' });
        if (Object.keys(this.errors).length) {
            return;
        }
        this.store(fields, 'Default email container saved.', () => this.container = null);
    }

    // ---- Appointment Confirmation ----

    editConfirmation(c: ConfirmationEmail) {
        this.open();
        this.confirmation = c;
        this.confirmationBody = this.values[c.field];
    }

    saveConfirmation() {
        const c = this.confirmation;
        this.store({ [c.field]: this.confirmationBody }, c.name + ' saved.', () => this.confirmation = null);
    }

    // ---- Shared ----

    private open() {
        this.errors = {};
        this.formError = '';
        this.message = '';
        this.showPlaceholders = false;
    }

    close() {
        this.container = null;
        this.confirmation = null;
    }

    private store(fields: any, done: string, closeDialog: () => void) {
        this.saving = true;
        this.formError = '';
        this.emails.save(fields).subscribe(
            () => {
                this.saving = false;
                Object.assign(this.values, fields);
                this.message = done;
                closeDialog();
            },
            err => {
                this.saving = false;
                this.formError = typeof err === 'string' ? err : 'The email could not be saved.';
            }
        );
    }
}
